import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import axiosClient from "../../axios";

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [edited, setEdited] = useState(false);

  const roleOptions = ["admin", "editor", "superadmin", "user", "distributor"];
  const premiumOptions = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axiosClient.get(`/api/users/${id}`);
        setUser(response.data);
      } catch (err) {
        setError("Failed to fetch user data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({
      ...prev,
      [name]: name === "premium" ? parseInt(value) : value,
    }));
    setEdited(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await axiosClient.put(`/api/users/${id}`, {
        role: user.role,
        premium: user.premium === 1 ? true : false, // Ensure boolean value
      });
      setSuccess("User updated successfully.");
      setEdited(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        setError("Unauthorized: Only superadmins can update users.");
      } else if (err.response?.status === 422) {
        // Show validation errors if available
        const errors = err.response.data.errors;
        setError(Object.values(errors).flat().join(" "));
      } else {
        setError(
          err.response?.data?.message || "Something went wrong while updating.",
        );
      }
    }
  };

  const handleBack = () => {
    navigate("/xl-admin/users");
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p>Loading user details...</p>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="danger">User not found</Alert>
        <Button onClick={handleBack}>Back to Users</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Button variant="secondary" onClick={handleBack} className="mb-4">
        Back to Users
      </Button>

      <h3 className="mb-4">User Details</h3>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={user.name || ""} readOnly />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={user.email || ""} readOnly />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formRole">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={user.role || ""}
                onChange={handleChange}
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="formPremium">
              <Form.Label>Premium Subscription</Form.Label>
              <Form.Select
                name="premium"
                value={user.premium || 0}
                onChange={handleChange}
              >
                {premiumOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formCreatedAt">
              <Form.Label>Registration Date</Form.Label>
              <Form.Control
                type="text"
                value={new Date(user.created_at).toLocaleString()}
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <Button variant="inchworm" type="submit" disabled={!edited}>
          Save Changes
        </Button>
      </Form>
    </Container>
  );
};

export default UserDetail;
